import type { UserRouter } from "replo-publisher/src/trpc/routers/user";
import type { User } from "schemas/generated/user";

import { getNamespacedQueryClient } from "@v2/trpc/trpc";

import { createTrpcRouterConfig } from "../config";

const {
  router: userRouter,
  client: userClient,
  utils: userUtils,
  options: userOptions,
} = createTrpcRouterConfig<UserRouter>({
  path: "user",
  queryClient: getNamespacedQueryClient("user"),
});

/**
 * Gets the most recently fetched cached user data from user.get, without
 * needing to provide specific query parameters. This can be used in
 * non-component contexts. Useful for times like in global error handling
 * where we need to imperatively access the most recently fetched user.
 *
 * @returns The most recently cached user data, or undefined if none exists
 */
export function getMostRecentViewingUser(): User | undefined {
  const queryClient = getNamespacedQueryClient("user");

  // Note (Noah, 2025-12-19): TRPC query keys look like:
  // [["get", { input: ..., type: "query" }]]
  // We find all matching queries and return the most recently updated one.
  const queries = queryClient.getQueryCache().findAll({
    queryKey: [["get"]],
  });

  if (queries.length === 0) {
    return undefined;
  }

  const mostRecent = queries.reduce((latest, query) => {
    if (!latest) {
      return query;
    }
    return query.state.dataUpdatedAt > latest.state.dataUpdatedAt
      ? query
      : latest;
  }, queries[0]);

  // Legacy cast - may or may not be necessary
  // eslint-disable-next-line replo/no-as-cast
  return mostRecent?.state.data as User | undefined;
}

export { userClient, userRouter, userUtils, userOptions };
